import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeLatest, takeLeading } from "redux-saga/effects";
import { API_URL } from "utils/constants";
import { getPaginationMeta } from "utils/helpers/pagination";
import { request } from "utils/request";

import {
  HydraulicManufacturerDetailResponse,
  HydraulicManufacturerForm,
  HydraulicManufacturerListResponse,
  HydraulicManufacturerUpdate,
} from "../types";
import { selectForm, selectPage, selectPerPage } from "./selector";
import { actions } from "./slice";

let isFetchingList = false;

export function* handleGetList() {
  if (isFetchingList) return;
  isFetchingList = true;

  try {
    const page: number = yield select(selectPage);
    const perPage: number = yield select(selectPerPage);

    const response: HydraulicManufacturerListResponse = yield call(
      request,
      `${API_URL}/hydraulic-wall-manufacturer?page=${page}&perPage=${perPage}`,
      { method: "GET" },
    );

    const rawData = response?.data || [];
    const meta = getPaginationMeta(page, perPage, rawData, response?.meta);
    const data = rawData.filter((item) =>
      Boolean(String(item?.hydraulicWallManufacturerId ?? "").trim()),
    );
    const payload = { data, meta };

    if (page === 1) {
      yield put(actions.getListSuccess(payload));
    } else {
      yield put(actions.getNextPageItemsSuccess(payload));
    }
  } catch (error) {
    const message = (error as Error).message;
    const page: number = yield select(selectPage);

    if (page === 1) {
      yield put(actions.getListError(message));
    } else {
      yield put(actions.getNextPageItemsError(message));
    }
  } finally {
    isFetchingList = false;
  }
}

export function* handleAdd() {
  const form: HydraulicManufacturerForm = yield select(selectForm);

  try {
    const response: HydraulicManufacturerDetailResponse = yield call(
      request,
      `${API_URL}/hydraulic-wall-manufacturer`,
      {
        method: "POST",
        body: JSON.stringify({
          hydraulicWallManufacturerId: form.code.trim(),
          hydraulicWallManufacturerName: form.description.trim(),
        }),
      },
    );

    yield put(actions.addSuccess(response.data));
  } catch (error) {
    yield put(actions.addError((error as Error).message));
  }
}

export function* handleUpdate(
  action: PayloadAction<HydraulicManufacturerUpdate>,
) {
  const { id, hydraulicWallManufacturerName } = action.payload;

  try {
    yield call(request, `${API_URL}/hydraulic-wall-manufacturer/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        hydraulicWallManufacturerName: hydraulicWallManufacturerName.trim(),
      }),
    });

    yield put(actions.updateSuccess());
    yield put(actions.getList());
  } catch (error) {
    yield put(actions.updateError((error as Error).message));
  }
}

export function* handleGetDetail(action: PayloadAction<string>) {
  const id = action.payload;

  try {
    const response: HydraulicManufacturerDetailResponse = yield call(
      request,
      `${API_URL}/hydraulic-wall-manufacturer/${id}`,
      { method: "GET" },
    );

    yield put(actions.getDetailSuccess(response?.data));
  } catch (error) {
    yield put(actions.getDetailError((error as Error).message));
  }
}

export function* handleDelete(action: PayloadAction<string>) {
  const id = action.payload;

  try {
    yield call(request, `${API_URL}/hydraulic-wall-manufacturer/${id}`, {
      method: "DELETE",
    });

    yield put(actions.deleteSuccess(id));
  } catch (error) {
    yield put(actions.deleteError((error as Error).message));
  }
}

export function* hydraulicManufacturerSaga() {
  yield takeLeading(
    [actions.getList.type, actions.getNextPageItems.type],
    handleGetList,
  );
  yield takeLatest(actions.add.type, handleAdd);
  yield takeLatest(actions.update.type, handleUpdate);
  yield takeLatest(actions.getDetail.type, handleGetDetail);
  yield takeLatest(actions.delete.type, handleDelete);
}

export default hydraulicManufacturerSaga;
